import { gql, useQuery } from '@apollo/client';
import { Box, Container, Spinner } from '@energiebespaarders/symbols';
import { Center } from '@energiebespaarders/symbols/helpers';
import { Check } from '@energiebespaarders/symbols/icons/solid';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFlag } from 'react-unleash-flags';
import { useKeyPress, useLocalStorage } from 'react-use';
import IntakeInterestsCard from '~/components/CustomerIntake/IntakeInterestsCard';
import config from '~/config';
import type { AnswerType, QuestionAnswerType } from '~/hooks/omniform/types';
import { useActiveHouseId } from '~/hooks/useActiveHouseId';
import { useActiveHouseProgress } from '~/hooks/useActiveHouseProgress';
import useEventTracker from '~/hooks/useEventTracker';
import { useMe } from '~/hooks/useMe';
import { usePersonalEnergyPrices } from '~/hooks/usePersonalEnergyPrices';
import type {
  getSelfIntakeHouseData,
  getSelfIntakeHouseDataVariables,
} from '~/types/generated/getSelfIntakeHouseData';
import type { me_me_Customer, me_me_Lead } from '~/types/generated/me';
import StyledBackgroundCard, { randomNumber } from '../Omniform/BackgroundCard';
import Omniform from '../Omniform/Omniform';
import PageLoading from '../PageLoading';
import SelfServiceIntakeDevTools from '../SelfServiceIntake/SelfServiceIntakeDevTools';
import IntakeAuthCard from './IntakeAuthCard';
import IntakeMarketingConsentCard from './IntakeMarketingConsentCard';
import { IntakeTextVariablesProvider } from './IntakeTextVariablesContext';
import WelcomeCard from './IntakeWelcomeCard';
import { getUpdatedTextVariablesFromAnswer, getVariablesFromHouse } from './textVariableUtils';
import YourHomeCard from './YourHomeCard';
import type { IntakeFlowData, IntakeTextVariables } from './types';
import type { OmniformSharedProps } from '../Omniform/OmniformContent';
import type { Solution } from '@energiebespaarders/constants';

export const GET_SELF_INTAKE_HOUSE_DATA = gql`
  query getSelfIntakeHouseData($houseId: ID!) {
    house(id: $houseId) {
      id
      constructionYear
      type
      area
      walls {
        cavity
      }
      crawlspace {
        compartments
      }
      energy {
        gasConsumption
        electricityConsumption
        electricityProduction
      }
      situation {
        id
        consumption {
          gas
          electricity
        }
      }
    }
  }
`;

type CurrentStep =
  | 'welkom'
  | 'woning'
  | 'login'
  | 'questions'
  | 'marketingConsent'
  | 'interests'
  | 'purpose';

interface CustomerIntakeControllerProps {
  formStyle?: OmniformSharedProps<any>['formStyle'];
  submitText?: string;
  submitIcon?: OmniformSharedProps<any>['submitIcon'];
  submitIconPosition?: OmniformSharedProps<any>['submitIconPosition'];
  textVariables?: Record<string, string>;
  initialStep?: CurrentStep;
  formKey?: string;
  formRevision?: number;
  onSubmitted?: () => void;
}

/** This component handles all the logic for fetching and controlling the data, cards and Omniforms required for the self-intake flow */
const CustomerIntakeController: React.FC<CustomerIntakeControllerProps> = ({
  formStyle,
  submitText,
  submitIcon = Check,
  submitIconPosition,
  textVariables: initialTextVariables,
  initialStep,
  onSubmitted,
  formKey = 'self-intake-complete',
  formRevision = 11,
}) => {
  const { me } = useMe<me_me_Lead | me_me_Customer>();
  const router = useRouter();
  const { activeHouseId } = useActiveHouseId();
  const { personalEnergyPrices } = usePersonalEnergyPrices();
  const trackEvent = useEventTracker();
  const [cmdPressed] = useKeyPress('Meta');
  const signupBeforeIntakeFlag = useFlag('signup-before-intake');
  const {
    data,
    refetch: refetchHouseProgress,
    startPolling: startPollingHouseProgress,
    stopPolling: stopPollingHouseProgress,
    networkStatus,
  } = useActiveHouseProgress();

  const hasMarketingConsent = me?.consent.some(consent => consent.title === 'marketing');

  /* AB test flag for showing the marketing consent in checkbox or separate question at the end of the intake */
  // const { getAbTestEntry } = useABTest();
  const usingConsentCard = useMemo(
    () =>
      dayjs().isBefore('2025-01-01') && // disable in 2025 for now, TODO: remove this check when/if we want it to be permanent
      config.isProduction && // prevent flakiness in e2e tests
      !hasMarketingConsent, // && getAbTestEntry('selfIntake')?.find(entry => entry.experiment === 'marketing-consent')?.variant === 'marketing-consent-card',
    [hasMarketingConsent],
  );

  const [textVariables, setTextVariables] = useState<IntakeTextVariables>(
    initialTextVariables ?? {},
  );
  const [interests, setSelectedInterests] = useLocalStorage<Solution[]>('selected-interests');

  const [flowData, setFlowData] = useState<IntakeFlowData>({
    answers: {} as Record<string, unknown>,
    constructionYear: 0,
    houseType: 0,
    houseId: '',
    customerInterests: interests ?? [],
  });
  const [shouldUpdateFlowData, setShouldUpdateFlowData] = useState(true);

  const { data: houseData, loading: houseLoading } = useQuery<
    getSelfIntakeHouseData,
    getSelfIntakeHouseDataVariables
  >(GET_SELF_INTAKE_HOUSE_DATA, {
    skip: !activeHouseId,
    variables: { houseId: activeHouseId },
    onCompleted: data => {
      setTextVariables(prev => ({ ...prev, ...getVariablesFromHouse(data, personalEnergyPrices) }));
      setShouldUpdateFlowData(true);
    },
  });

  const handleUpdateTextVariables = useCallback(
    (questionKey: string, answer: AnswerType<QuestionAnswerType>) => {
      setTextVariables(prev => ({
        ...prev,
        ...getUpdatedTextVariablesFromAnswer(questionKey, answer, personalEnergyPrices),
      }));
    },
    [personalEnergyPrices],
  );

  const getUpdatedFlowData = (
    currentFlowData: IntakeFlowData,
    questionKey: string,
    answer: AnswerType<QuestionAnswerType>,
  ) => ({
    ...currentFlowData,
    answers: { ...currentFlowData.answers, [questionKey]: answer },
  });

  useEffect(() => {
    if (interests && interests !== flowData.customerInterests) {
      setShouldUpdateFlowData(true);
    }
    if (houseData && shouldUpdateFlowData) {
      const { constructionYear } = getVariablesFromHouse(houseData, personalEnergyPrices);
      setFlowData({
        answers: {} as Record<string, unknown>,
        constructionYear,
        houseType: houseData.house.type,
        houseId: activeHouseId,
        customerInterests: interests ?? flowData.customerInterests,
      });
      setShouldUpdateFlowData(false);
    }
  }, [activeHouseId, houseData, personalEnergyPrices, shouldUpdateFlowData, interests]);

  // INTAKE INTRO CARDS
  const customerIntakeInfo = data?.customerIntakeByHouse;
  const [currentStep, setCurrentStep] = useState<CurrentStep>(initialStep || 'welkom');
  useEffect(() => {
    if (!activeHouseId) return;
    // Set the intro state based on the hash in the URL on load
    const hash = window.location.hash;
    if (currentStep !== 'woning' && hash === '#woning') setCurrentStep('woning');
    // Also set the intro state to login if the user is already logged in, the IntakeAuthCard will handle the rest
    if (currentStep !== 'login' && (hash === '#login' || hash === '#start')) {
      signupBeforeIntakeFlag?.enabled ? setCurrentStep('login') : setCurrentStep('questions');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [addressLoaded] = useLocalStorage<boolean>('addressLoaded', false);

  // We only want to send the events once (per session), so we store that in local storage
  const [beginEventSent, setBeginEventSent] = useLocalStorage('intakeBeginEventSent', false);
  const [completeEventSent, setCompleteEventSent] = useLocalStorage(
    'intakeCompleteEventSent',
    false,
  );

  const partnerAgentReferrer = useMemo(() => {
    const queryString = new URLSearchParams(window.location.search);
    return queryString.get('pa_id') || '';
  }, []);

  const sendIntakeBeginEvent = useCallback(() => {
    if (beginEventSent) return;
    trackEvent('intake_begin', {
      house_id: activeHouseId,
      method: 'omniform',
      ...(partnerAgentReferrer ? { partner_agent_id: partnerAgentReferrer } : {}),
    });
    setBeginEventSent(true);
  }, [beginEventSent, trackEvent, activeHouseId, partnerAgentReferrer, setBeginEventSent]);

  const sendIntakeCompleteEvent = useCallback(() => {
    if (completeEventSent) return;
    trackEvent('intake_complete', {
      house_id: activeHouseId,
      method: 'omniform',
      ...(partnerAgentReferrer ? { partner_agent_id: partnerAgentReferrer } : {}),
    });
    setCompleteEventSent(true);
  }, [completeEventSent, trackEvent, activeHouseId, partnerAgentReferrer, setCompleteEventSent]);

  const goToWelcome = useCallback(() => {
    setCurrentStep('welkom');
    window.location.hash = '';
  }, []);
  const goToHomeCard = useCallback(() => {
    setCurrentStep('woning');
    window.location.hash = '#woning';
  }, []);
  const goToInterests = useCallback(() => {
    setCurrentStep('interests');
    window.location.hash = '#interests';
  }, []);
  const goToQuestions = useCallback(() => {
    setCurrentStep('questions');
    window.location.hash = '';
    sendIntakeBeginEvent();
  }, [sendIntakeBeginEvent]);

  const goToAuth = useCallback(() => {
    setCurrentStep('login');
    window.location.hash = '#login';
  }, []);

  const goToEstimates = useCallback(() => {
    void router.push('/intake/indicaties');
  }, [router]);

  const [submitted, setSubmitted] = useState(false);

  const handleFinishOmniform = useCallback(() => {
    sendIntakeCompleteEvent();
    void refetchHouseProgress?.();

    if (usingConsentCard) return setCurrentStep('marketingConsent'); // If we're using the consent card, we want to show that first

    startPollingHouseProgress(500);
    setSubmitted(true);
    onSubmitted?.();
  }, [
    sendIntakeCompleteEvent,
    refetchHouseProgress,
    usingConsentCard,
    startPollingHouseProgress,
    onSubmitted,
  ]);

  useEffect(() => {
    if (
      submitted &&
      customerIntakeInfo?.submittedOn &&
      dayjs(customerIntakeInfo.submittedOn).diff(dayjs(), 'seconds') >= -60
    ) {
      stopPollingHouseProgress();
      goToEstimates();
    }
  }, [
    networkStatus,
    customerIntakeInfo?.submittedOn,
    goToEstimates,
    submitted,
    stopPollingHouseProgress,
  ]);

  if (activeHouseId && (!houseData?.house || houseLoading)) return <PageLoading />;

  const cardOrder: CurrentStep[] = [
    'welkom',
    'woning',
    'login',
    'questions',
    ...((usingConsentCard ? ['marketingConsent'] : []) as CurrentStep[]),
  ];

  const isWebdriver = typeof navigator === 'undefined' ? false : navigator.webdriver;

  return (
    <IntakeTextVariablesProvider textVariables={textVariables}>
      <Box style={{ position: 'relative' }} mb={8}>
        {currentStep === 'welkom' || !me ? (
          <WelcomeCard onNext={goToHomeCard} />
        ) : currentStep === 'woning' ? (
          <YourHomeCard
            onPrevious={initialStep === 'questions' ? () => null : goToWelcome}
            onNext={signupBeforeIntakeFlag?.enabled ? goToAuth : goToInterests}
            hideLoading={!!customerIntakeInfo?.submittedOn || !!addressLoaded || isWebdriver}
          />
        ) : currentStep === 'login' ? (
          <IntakeAuthCard
            onPrevious={goToHomeCard}
            onLoginSuccess={goToInterests}
            hideMarketingConsent={usingConsentCard || hasMarketingConsent}
          />
        ) : currentStep === 'interests' ? (
          <IntakeInterestsCard
            onNext={goToQuestions}
            onPrevious={goToHomeCard}
            onSetSelectedInterests={setSelectedInterests}
          />
        ) : currentStep === 'questions' ? (
          <Omniform<IntakeFlowData>
            formKey={formKey}
            formRevision={formRevision} // since we have to update e2e tests anyway we should keep the version of the form static too
            onSubmit={handleFinishOmniform}
            submitText={submitText || 'Voltooien'}
            submitIcon={submitIcon}
            submitIconPosition={submitIconPosition}
            formStyle={formStyle || 'questionCards'}
            initialFlowData={flowData}
            updateFlowData={getUpdatedFlowData}
            textVariables={textVariables}
            onAnswer={handleUpdateTextVariables}
            onPreviousFromStart={initialStep === 'questions' ? undefined : goToInterests}
          />
        ) : currentStep === 'marketingConsent' ? (
          <IntakeMarketingConsentCard onNext={goToEstimates} onPrevious={goToQuestions} />
        ) : (
          <Center block>
            <Spinner />
          </Center>
        )}
        {currentStep !== 'questions' && currentStep !== 'marketingConsent' && (
          <>
            <StyledBackgroundCard
              key={`${currentStep}-bg-1`}
              bgColor="rgb(250, 250, 250)"
              $depth={1}
              animation="none"
              $randomizer={
                (randomNumber(1) > 0.5 ? 1 : -1) * randomNumber(cardOrder.indexOf(currentStep))
              }
            />
            <StyledBackgroundCard
              key={`${currentStep}-bg-2`}
              bgColor="rgb(250, 250, 250)"
              $depth={2}
              animation="none"
              $randomizer={
                (randomNumber(1) > 0.5 ? 1 : -1) * randomNumber(cardOrder.indexOf(currentStep))
              }
            />
            <StyledBackgroundCard
              key={`${currentStep}-bg-3`}
              bgColor="rgb(250, 250, 250)"
              $depth={3}
              animation="none"
              $randomizer={
                (randomNumber(1) > 0.5 ? 1 : -1) * randomNumber(cardOrder.indexOf(currentStep))
              }
            />
          </>
        )}
      </Box>
      {!config.isProduction && cmdPressed && (
        <Container size="sm" pt={5}>
          <SelfServiceIntakeDevTools onSubmit={() => router.push('/intake/indicaties')} />
        </Container>
      )}
    </IntakeTextVariablesProvider>
  );
};

export default CustomerIntakeController;
